import Layout from "../Layout";
import { Hero } from "../../components/marketing/Hero";
import { HowItWorks } from "../../components/marketing/HowItWorks";
import { WhatWeSupport } from "../../components/marketing/WhatWeSupport";
import { Security } from "../../components/marketing/Security";
import { Pricing } from "../../components/marketing/Pricing";
import { BuiltFor } from "../../components/marketing/BuiltFor";
import { LatestArticles, type Article } from "../../components/marketing/LatestArticles";
import { FAQ, type FAQItem } from "../../components/marketing/FAQ";
import { CallToAction } from "../../components/marketing/CallToAction";

const FAQ_ITEMS: readonly FAQItem[] = [
  {
    question: "How do you use Terraform to automatically reduce AWS costs?",
    answer:
      "Connect your AWS accounts and Terraform repos. Infralyst analyzes CloudWatch metrics for EC2, Auto Scaling Groups, and ECS Fargate, and detects EBS gp2 volumes to migrate to gp3. It generates Terraform pull requests with the exact change. Your team reviews and merges like any other PR. No manual analysis, no spreadsheets, no dashboards to watch.",
  },
  {
    question: "What is AWS rightsizing and why does it reduce costs?",
    answer:
      "Rightsizing means matching each resource's instance type or task size to its actual usage instead of what was guessed at launch. Most teams over-provision by 20-40% because they size for peak traffic and never revisit. Infralyst continuously analyzes CPU and memory utilization across EC2, Auto Scaling Groups, and ECS Fargate, then generates the Terraform code to resize each resource.",
  },
  {
    question: "How much can you save by rightsizing AWS infrastructure?",
    answer:
      "Most teams find 20-40% of their EC2 compute is oversized. A single instance type change can save $50-200/month, and savings compound across dozens or hundreds of resources. Infralyst uses conservative thresholds (CPU and memory P99.5 below 40%) to make sure recommendations are safe before they become PRs.",
  },
  {
    question: "Why do most teams overspend on AWS compute?",
    answer:
      "Teams provision for peak traffic, then never revisit. Instance types chosen during an initial launch or scaling event stay locked in long after usage patterns change. Without continuous analysis, there's no trigger to resize. Infralyst runs that analysis automatically across all your AWS accounts and regions.",
  },
  {
    question: "Do I need a FinOps team to reduce cloud costs?",
    answer:
      "No. A dedicated FinOps function helps at scale, but most teams can capture significant savings by embedding cost optimization into their existing engineering workflow. Infralyst gives you that without hiring, dashboards, or spreadsheet analysis. Connect your AWS accounts, and savings show up as reviewable Terraform PRs.",
  },
  {
    question: "How do you reduce AWS costs without changing architecture?",
    answer:
      "Rightsizing keeps your existing architecture intact. You don't change what runs or how it's deployed. You change the size of the instances and tasks running it. Infralyst analyzes real CloudWatch utilization, finds resources where CPU and memory P99.5 are well below capacity, and generates a Terraform PR that changes the instance type or task size. Your deployment model, networking, and application code stay exactly the same.",
  },
  {
    question:
      "What's the difference between a cost dashboard and automated AWS rightsizing?",
    answer:
      "Cost dashboards show you graphs and spending trends, but leave the analysis and implementation to you. Automated rightsizing does the work: Infralyst analyzes CloudWatch utilization against fixed thresholds, picks a target instance type, and generates a Terraform PR with the exact code change. You go from insight to merged code without spreadsheets or manual CloudWatch digging.",
  },
  {
    question:
      "Can you reduce AWS costs without installing agents on your instances?",
    answer:
      "Yes. Infralyst reads CloudWatch metrics and EC2 metadata through a read-only IAM role. No agents, sidecars, or daemons on your instances. The only exception is memory metrics for EC2: AWS doesn't publish those by default, so you need the CloudWatch Agent installed if you want memory-aware recommendations. Infralyst works without it but flags the gap.",
  },
  {
    question:
      "Does automated rightsizing work with existing Terraform CI/CD pipelines?",
    answer:
      "Yes. Infralyst opens a standard pull request in your GitHub repo. Your existing CI (plan, validate, policy checks) runs on the PR branch like any other change. When your team merges, your existing deployment pipeline applies it. Infralyst doesn't bypass or replace anything in your workflow.",
  },
  {
    question:
      "Does downsizing EC2 instances affect Reserved Instance or Savings Plan coverage?",
    answer:
      "It can. Instance-size-flexible Reserved Instances and Compute Savings Plans automatically adjust to smaller sizes within the same family, so coverage usually carries over. Instance-specific RIs tied to an exact instance type won't apply after a resize. Check your RI and Savings Plan terms before merging a downsize to avoid paying on-demand rates for a covered instance.",
  },
] as const;

interface Props {
  articles: Article[];
}

export default function Home({ articles }: Props) {
  return (
    <Layout>
      <Hero />
      <HowItWorks />
      <WhatWeSupport />
      <Security />
      <Pricing />
      <BuiltFor />
      <LatestArticles articles={articles} />
      <FAQ
        items={FAQ_ITEMS}
        title="AWS Cost Optimization FAQ"
        highlightWord="FAQ"
        className="py-16 px-4"
      />
      <CallToAction />
    </Layout>
  );
}
